import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { BookOpen, Eye, EyeOff, Loader2, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const loginSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(1, 'Mot de passe requis'),
});

const registerSchema = z.object({
  first_name: z.string().min(2, 'Prénom requis (min. 2 caractères)'),
  last_name: z.string().min(2, 'Nom requis (min. 2 caractères)'),
  email: z.string().email('Email invalide'),
  password: z.string().min(6, 'Mot de passe trop court (min. 6 caractères)'),
  confirmPassword: z.string(),
  phone: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
});

export default function LoginPage() {
  const { t, language, toggleLanguage } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register: registerUser } = useAuth();
  const navigate = useNavigate();

  const loginForm = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const registerForm = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
    },
  });

  const handleLogin = async (data) => {
    setLoading(true);
    try {
      await login(data.email, data.password);
      toast.success(t('success') + '!');
      navigate('/');
    } catch (err) {
      toast.error(err.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (data) => {
    setLoading(true);
    try {
      const { confirmPassword, ...registerData } = data;
      await registerUser({ ...registerData, role: 'admin' });
      toast.success('Compte créé avec succès !');
      navigate('/');
    } catch (err) {
      toast.error(err.message || 'Erreur d\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Language Toggle - Fixed top right */}
      <Button
        variant="outline"
        size="icon"
        onClick={toggleLanguage}
        className="fixed top-4 right-4 z-50 bg-white shadow-md"
        title={language === 'fr' ? 'العربية' : 'Français'}
      >
        <Globe className="h-4 w-4" />
      </Button>

      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary p-12 flex-col justify-between geometric-pattern">
        <div>
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="CARFEIS" className="w-16 h-16 rounded-full object-cover border-2 border-white/20" />
            <div>
              <h1 className="font-heading font-bold text-2xl text-white">CARFEIS</h1>
              <p className="text-primary-100 text-sm">Daara Serigne Chouhaibou Mbacké</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-6">
          <h2 className="font-heading font-bold text-4xl text-white leading-tight">
            {language === 'ar' ? 'إدارة مدرستك\nالفرنسية-العربية والكلاسيكية' : 'Gérez votre école\nCoranique & Classique'}
          </h2>
          <p className="text-primary-100 text-lg max-w-md">
            {language === 'ar' 
              ? 'منصة حديثة لمتابعة الطلاب والتقييمات القرآنية والإدارة المالية لمؤسستك.'
              : 'Une plateforme moderne pour le suivi des élèves, des évaluations coraniques, et la gestion financière de votre établissement.'}
          </p>
          
          <div className="flex gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-white">500+</p>
              <p className="text-primary-100 text-sm">{t('students')}</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">30+</p>
              <p className="text-primary-100 text-sm">{t('teachers')}</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">114</p>
              <p className="text-primary-100 text-sm">{language === 'ar' ? 'سورة' : 'Sourates'}</p>
            </div>
          </div>
        </div>

        <p className="text-primary-200 text-sm">
          © 2024 CARFEIS - {language === 'ar' ? 'جميع الحقوق محفوظة' : 'Tous droits réservés'}
        </p>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8 justify-center">
            <img src="/logo.png" alt="CARFEIS" className="w-12 h-12 rounded-full object-cover" />
            <h1 className="font-heading font-bold text-xl">CARFEIS</h1>
          </div>

          <Card className="border-0 shadow-card-hover">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="font-heading text-2xl">
                {isLogin ? t('loginTitle') : (language === 'ar' ? 'إنشاء حساب' : 'Créer un compte')}
              </CardTitle>
              <CardDescription>
                {isLogin
                  ? t('loginSubtitle')
                  : (language === 'ar' ? 'أكمل النموذج لإنشاء حساب المسؤول' : 'Remplissez le formulaire pour créer votre compte administrateur')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLogin ? (
                <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={language === 'ar' ? 'بريدك@email.com' : 'votre@email.com'}
                      {...loginForm.register('email')}
                      className="field-focus"
                      data-testid="login-email"
                    />
                    {loginForm.formState.errors.email && (
                      <p className="text-sm text-destructive">{loginForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">{t('password')}</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        {...loginForm.register('password')}
                        className="field-focus pr-10"
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="text-sm text-destructive">{loginForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-full" 
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {t('loginButton')}
                  </Button>
                </form>
              ) : (
                <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">{t('firstName')}</Label>
                      <Input
                        id="first_name"
                        placeholder={t('firstName')}
                        {...registerForm.register('first_name')}
                        className="field-focus"
                        data-testid="register-firstname"
                      />
                      {registerForm.formState.errors.first_name && (
                        <p className="text-xs text-destructive">{registerForm.formState.errors.first_name.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Nom</Label>
                      <Input
                        id="last_name"
                        placeholder="Nom"
                        {...registerForm.register('last_name')}
                        className="field-focus"
                        data-testid="register-lastname"
                      />
                      {registerForm.formState.errors.last_name && (
                        <p className="text-xs text-destructive">{registerForm.formState.errors.last_name.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-email">Email</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="votre@email.com"
                      {...registerForm.register('email')}
                      className="field-focus"
                      data-testid="register-email"
                    />
                    {registerForm.formState.errors.email && (
                      <p className="text-sm text-destructive">{registerForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone (optionnel)</Label>
                    <Input
                      id="phone"
                      placeholder="+221 77 123 45 67"
                      {...registerForm.register('phone')}
                      className="field-focus"
                      data-testid="register-phone"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-password">Mot de passe</Label>
                    <Input
                      id="reg-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      {...registerForm.register('password')}
                      className="field-focus"
                      data-testid="register-password"
                    />
                    {registerForm.formState.errors.password && (
                      <p className="text-sm text-destructive">{registerForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      {...registerForm.register('confirmPassword')}
                      className="field-focus"
                      data-testid="register-confirm-password"
                    />
                    {registerForm.formState.errors.confirmPassword && (
                      <p className="text-sm text-destructive">{registerForm.formState.errors.confirmPassword.message}</p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-full" 
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Créer le compte
                  </Button>
                </form>
              )}

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  data-testid="toggle-auth-mode"
                >
                  {isLogin ? "Pas de compte ? Créer un compte" : "Déjà un compte ? Se connecter"}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
